import { useCallback, useState } from 'react';

import { Link, useSearchParams } from 'react-router';

import { formatPresentationDate, formatTime, isFiniteNumber } from '@core';
import { Tag } from '@data';
import {
    useActivity,
    useDeleteActivity,
    useLayoutById,
    useSwingThreadsByEntity,
    useThreadMessageUnreadNotifications,
} from '@queries';
import { Box, BubbleTextIcon, ClockIcon, Heading3, Heading4, MapPinIcon, StarIcon, TrashIcon, colors, u } from '@ui';

import * as css from './ActivityListItem.css';
import { ActivityListTag } from './ActivityListTag';
import { DeleteActivityModal } from './DeleteActivityModal';

export type ActivityListItemProps = {
    tags?: Tag[];
    startTime: string;
    clubShortName?: string;
    starredSwingsCount: number;
    noOfSwings: number;
    activityId: number;
    ownerId: string;
    currentActivityId: number;
    lastSelectedActivityId?: number;
    hasLiveMeasurements?: boolean;
    getActivityLink: (activityId: number) => string;
    onSelect?: () => void;
};

export const ActivityListItem = ({
    clubShortName,
    startTime,
    tags,
    starredSwingsCount,
    noOfSwings,
    activityId,
    ownerId,
    currentActivityId,
    lastSelectedActivityId,
    hasLiveMeasurements,
    getActivityLink,
    onSelect,
}: ActivityListItemProps) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const { mutateAsync: deleteActivity } = useDeleteActivity(ownerId);
    const { data: unreadNotifications } = useThreadMessageUnreadNotifications();
    const unreadNotificationCount = unreadNotifications?.activities?.[activityId]?.length;
    const { data: threadIdsInActivity } = useSwingThreadsByEntity(activityId);
    const threadCount = threadIdsInActivity?.length ?? 0;
    const handleConfirm = useCallback(async () => {
        await deleteActivity(activityId);
    }, [activityId, deleteActivity]);

    const { data: activity } = useActivity(activityId);
    const { data: layout } = useLayoutById(activity?.layoutId ?? -1);

    return (
        <>
            <div className={css.container} data-activity-id={activityId}>
                <Link
                    to={{ pathname: getActivityLink(activityId), search: searchParams.toString() }}
                    className={css.linkPrimary({ selected: lastSelectedActivityId === activityId })}
                    onClick={onSelect}
                >
                    <Box display="flex" gap={8}>
                        <Heading3>{formatPresentationDate(startTime)}</Heading3>
                        <ClockIcon color={colors.bluegray[600]} width={16} />
                        <Heading3>{formatTime(startTime)}</Heading3>
                    </Box>
                    <Box display="flex" width="100%" justifyContent="space-between">
                        <Box display="flex" gap={8}>
                            {currentActivityId === activityId && (
                                <ActivityListTag state="success">CURRENT ACTIVITY</ActivityListTag>
                            )}
                            {clubShortName && <ActivityListTag>{clubShortName}</ActivityListTag>}
                            {hasLiveMeasurements && <ActivityListTag state="primary">Live</ActivityListTag>}
                            {noOfSwings > 0 && (
                                <div className={css.swingsPill({ hasStars: !!starredSwingsCount })}>
                                    <Box display="flex">
                                        <Heading4>
                                            {noOfSwings} {noOfSwings === 1 ? 'swing' : 'swings'}
                                        </Heading4>
                                    </Box>
                                    {!!starredSwingsCount && (
                                        <div className={css.starBadge}>
                                            <Heading4>{starredSwingsCount}</Heading4>
                                            <StarIcon weight="fill" size="8" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {tags?.map((tag) => {
                                return <ActivityListTag key={tag.id}>{tag.name}</ActivityListTag>;
                            })}
                            {layout && <ActivityListTag>{layout.name}</ActivityListTag>}
                        </Box>
                        <Box alignSelf="flex-end" display="flex" gap={u(2)}>
                            {threadCount ? (
                                <div className={css.swingsPill({ hasStars: false })}>
                                    <Box display="flex" alignItems="center" gap={u(0.5)}>
                                        <MapPinIcon color={colors.bluegray[500]} />
                                        <Heading4>
                                            {`${threadCount} ${threadCount === 1 ? 'Thread' : 'Threads'}`}
                                        </Heading4>
                                    </Box>
                                </div>
                            ) : null}
                            {isFiniteNumber(unreadNotificationCount) ? (
                                <div className={css.swingsPill({ hasStars: false })}>
                                    <Box display="flex" alignItems="center" gap={u(0.5)}>
                                        <BubbleTextIcon color={colors.teal[500]} />
                                        <Heading4> {unreadNotificationCount} Unread</Heading4>
                                    </Box>
                                </div>
                            ) : null}
                            <button
                                className={css.deleteButton}
                                onClick={(evt) => {
                                    evt.stopPropagation();
                                    evt.preventDefault();
                                    setIsDeleteModalOpen(true);
                                }}
                            >
                                <TrashIcon color="inherit" size={u(1.5)} />
                                delete
                            </button>
                        </Box>
                    </Box>
                </Link>
            </div>
            {isDeleteModalOpen && (
                <DeleteActivityModal
                    activityDate={`${formatPresentationDate(startTime)} ${formatTime(startTime)}`}
                    onConfirm={handleConfirm}
                    open={isDeleteModalOpen}
                    onDismiss={() => setIsDeleteModalOpen(false)}
                    ownerId={ownerId}
                />
            )}
        </>
    );
};
